/**
 * Suppress the benign "ResizeObserver loop …" runtime errors.
 *
 * "ResizeObserver loop completed with undelivered notifications." (and the older
 * "ResizeObserver loop limit exceeded") are NOT real errors — the browser emits
 * them when a ResizeObserver callback mutates layout, so it defers the remaining
 * notifications to the next frame. They have no functional impact and never
 * surface an overlay in production. However, CRA / webpack-dev-server escalates
 * any uncaught window `error` into a blocking dev-overlay, so a simple window
 * resize spams the overlay.
 *
 * This swallows ONLY those two messages (and dismisses the dev overlay if it
 * already rendered). It does NOT patch ResizeObserver, so all resize behaviour —
 * charts, orderbook, resizable panels — is completely unchanged.
 */
const RESIZE_OBSERVER_ERROR =
  /^ResizeObserver loop (limit exceeded|completed with undelivered notifications)/;

const isBenignResizeObserverError = (message?: string): boolean =>
  !!message && RESIZE_OBSERVER_ERROR.test(message);

const dismissDevOverlay = (): void => {
  if (process.env.NODE_ENV === "production") return;
  // webpack-dev-server v4 (CRA 5) renders the runtime-error overlay in these nodes.
  [
    "webpack-dev-server-client-overlay",
    "webpack-dev-server-client-overlay-div",
  ].forEach((id) => document.getElementById(id)?.remove());
};

if (typeof window !== "undefined") {
  window.addEventListener("error", (event) => {
    if (isBenignResizeObserverError(event.message)) {
      event.stopImmediatePropagation();
      event.preventDefault();
      dismissDevOverlay();
    }
  });

  // Some browsers surface it as an unhandled rejection instead.
  window.addEventListener("unhandledrejection", (event) => {
    const message =
      typeof event.reason === "string" ? event.reason : event.reason?.message;
    if (isBenignResizeObserverError(message)) {
      event.preventDefault();
      dismissDevOverlay();
    }
  });
}

export {};
